import React, { useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth, useOrganizationList } from '@clerk/clerk-react';
import { apiClient } from '../lib/api';

export default function ProtectedRoute({ children }) {
  const { isLoaded, isSignedIn } = useAuth();
  const { userMemberships, setActive } = useOrganizationList({ userMemberships: true });
  const linkAttemptedRef = useRef(false);

  // Handle pending tier linking after registration/login
  // This runs once when the user is signed in
  useEffect(() => {
    const linkStripeSubscription = async () => {
      const pendingTier = localStorage.getItem('pendingTierLink');

      // Skip if no pending tier link or already attempted
      if (!pendingTier || linkAttemptedRef.current) {
        return;
      }

      // Mark as attempted to prevent duplicate calls
      linkAttemptedRef.current = true;

      try {
        console.log('Attempting to link Stripe subscription for tier:', pendingTier);
        const result = await apiClient.linkStripeCustomer();

        if (result.status === 'success') {
          console.log('Successfully linked Stripe subscription:', result.message);
        } else if (result.status === 'already_linked') {
          console.log('User already has paid tier:', result.message);
        } else if (result.status === 'not_found') {
          console.log('No matching Stripe subscription found:', result.message);
        } else {
          console.warn('Tier linking failed:', result.message);
        }
      } catch (error) {
        console.error('Error linking Stripe subscription:', error);
      } finally {
        // Clear the flag regardless of result
        localStorage.removeItem('pendingTierLink');
      }
    };

    // Only attempt linking when user is signed in
    if (isLoaded && isSignedIn) {
      linkStripeSubscription();
    }
  }, [isLoaded, isSignedIn]);

  // Auto-activate org session so JWT includes org_id/org_slug
  // This is needed for backend tenant resolution
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!userMemberships?.data?.length) return;
    if (!setActive) return;

    const firstOrg = userMemberships.data[0].organization;
    // Only activate if no org is currently active
    // Check by looking at the Clerk session's active org
    const currentOrgId = window.Clerk?.session?.lastActiveOrganizationId;
    if (!currentOrgId && firstOrg?.id) {
      setActive({ organization: firstOrg.id }).catch((err) => {
        console.warn('Failed to auto-activate org:', err);
      });
    }
  }, [isLoaded, isSignedIn, userMemberships?.data, setActive]);

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/login" replace />;
  }

  return children;
}
